export interface Appointment {
  id: number;
  patient_id: number;
  doctor_id: number;
  clinic_id: number;
  appointment_date: string;
  appointment_time: string;
  formatted_date: string;
  formatted_time: string;
  duration: number;
  type: string;
  type_label: string;
  status: string;
  status_label: string;
  status_color: string;
  reason?: string;
  notes?: string;
  patient_notes?: string;
  whatsapp_reminder: boolean;
  can_be_rescheduled: boolean;
  can_be_cancelled: boolean;
  service_id?: number;
  total_fee?: number;
  custom_pricing_applied?: boolean;   // ADD THIS
  custom_pricing_reason?: string;     // ADD THIS
  has_custom_pricing?: boolean;
  amount_to_collect?: number;
  original_total_fee?: number;
  final_total_amount?: number;
  custom_pricing?: any[];
  pricing_summary?: {
    original_total: number;
    final_total: number;
    total_savings: number;
    services_count: number;
    custom_priced_services_count: number;
  };
  
  patient: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    phone: string;
    email?: string;
    mother_name?: string;
  };
  doctor: {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    specialty?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
   customPrice?: number;     // ADD THIS
  pricingReason?: string;   // ADD THIS
}
export interface AppointmentEditRequest {
  password: string;
  services: {
    service_id: number;
    custom_price?: number;
    pricing_notes?: string;
    referrer_name?: string;
  }[];
  edit_reason: string;
}

export interface MarkIncompleteRequest {
  password: string;
  reason?: string;
}

export interface NewPatientData {
  first_name: string;
  last_name: string;
  phone: string;
}

export interface AppointmentFormData {
  patient_id: number;
  doctor_id: number;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  type: string;
  reason: string;
  notes: string;
  patient_notes: string;
  whatsapp_reminder: boolean;
}

export interface TimeSlot {
  time: string;
  formatted_time: string;
  available: boolean;
}

class AppointmentService {
  private static baseURL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };
    
    try {
      const response = await fetch(`${this.baseURL}${url}`, config);
      
      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        if (contentType.includes('application/json')) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            
            // Handle validation errors specifically
            if (response.status === 422 && errorData.errors) {
              const validationErrors = Object.values(errorData.errors).flat();
              errorMessage = validationErrors.join(', ');
            }
          } catch (jsonError) {
            console.log('Failed to parse JSON error response:', jsonError);
          }
        } else if (contentType.includes('text/html')) {
          if (response.status === 404) {
            errorMessage = 'API endpoint not found. Please check your route configuration.';
          } else if (response.status === 500) {
            errorMessage = 'Internal server error. Please check your backend logs.';
          } else if (response.status === 403) {
            errorMessage = 'Access denied. You may not have permission to book appointments for this doctor.';
          } else if (response.status === 422) {
            errorMessage = 'Validation error. Please check the appointment data.';
          } else {
            errorMessage = `Server error (${response.status}). Server returned HTML instead of JSON.`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
      
    } catch (error: any) {
      console.error('Request failed:', error);
      
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your internet connection and backend server.');
      } else if (error.message.includes('Unexpected token')) {
        throw new Error('Server returned invalid response. Please check your backend configuration.');
      }
      
      throw error;
    }
  }

  // Get appointments with filters
   static async getAppointments(params: {
    start_date?: string;
    end_date?: string;
    date?: string;
    doctor_id?: number;patient_id?: number;
    status?: string;
    search?: string;
  } = {}): Promise<{ data: Appointment[] }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.request(`/appointments?${queryParams}`);
    return response;
  }

  // Get single appointment
  static async getAppointment(id: number): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}`);
    return response.data;
  }

  // Add custom pricing save method

  // Create appointment
  static async createAppointment(
    data: AppointmentFormData,
    newPatientData?: NewPatientData
  ): Promise<{
    appointment: Appointment;
    patient_created?: {
      id: number;
      name: string;
      phone: string;
      note: string;
    };
  }> {
    // Validate required fields before making request
    if (!newPatientData && !data.patient_id) {
      throw new Error('Patient is required');
    }
    if (!data.doctor_id) {
      throw new Error('Doctor is required');
    }
    if (!data.appointment_date) {
      throw new Error('Appointment date is required');
    }
    if (!data.appointment_time) {
      throw new Error('Appointment time is required');
    }

    // Validate new patient data if provided
    if (newPatientData) {
      if (!newPatientData.first_name?.trim()) {
        throw new Error('Patient first name is required');
      }
      if (!newPatientData.last_name?.trim()) {
        throw new Error('Patient last name is required');
      }
    }
    
    // Prepare request body
    const requestBody: any = { ...data };
    
    if (newPatientData) {
      requestBody.new_patient = {
        first_name: newPatientData.first_name.trim(),
        last_name: newPatientData.last_name.trim(),
        phone: newPatientData.phone?.trim() || '',
      };
      
      // Remove patient_id since we're creating a new patient
      delete requestBody.patient_id;
    }
    
    // console.log('Creating appointment with data:', requestBody);
    
    const response = await this.request('/appointments', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });

    return {
      appointment: response.data,
      patient_created: response.patient_created || undefined,
    };
  }

  // Update appointment
  // Update appointment - FIXED to handle new patient creation
static async updateAppointment(
  id: number, 
  data: Partial<AppointmentFormData>,
  newPatientData?: NewPatientData
): Promise<{
  appointment: Appointment;
  patient_created?: {
    id: number;
    name: string;
    phone: string;
    note: string;
  };
}> {
  // Validate new patient data if provided
  if (newPatientData) {
    if (!newPatientData.first_name?.trim()) {
      throw new Error('Patient first name is required');
    }
    if (!newPatientData.last_name?.trim()) {
      throw new Error('Patient last name is required');
    }
  }
  
  // Prepare request body
  const requestBody: any = { ...data };
  
  if (newPatientData) {
    requestBody.new_patient = {
      first_name: newPatientData.first_name.trim(),
      last_name: newPatientData.last_name.trim(),
      phone: newPatientData.phone?.trim() || '',
    };
    
    // Remove patient_id since we're creating a new patient
    delete requestBody.patient_id;
  }
  
  console.log('Updating appointment with data:', requestBody);
  
  const response = await this.request(`/appointments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(requestBody),
  });

  return {
    appointment: response.data,
    patient_created: response.patient_created || undefined,
  };
}

  // Delete appointment
   static async deleteAppointment(id: number): Promise<void> {
    await this.request(`/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  // Confirm appointment
   static async confirmAppointment(id: number): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/confirm`, {
      method: 'POST',
    });
    return response.data;
  }


  // Mark appointment as arrived
  static async arriveAppointment(id: number): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/arrive`, {
      method: 'POST',
    });
    return response.data;
  }

  // Complete appointment (original method - without services)
  static async completeAppointment(id: number): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/complete`, {
      method: 'POST',
    });
    return response.data;
  }

  // Cancel appointment
  static async cancelAppointment(id: number, reason?: string): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return response.data;
  }
  // Complete appointment with services and automatic revenue creation=
  // Complete appointment with services and automatic revenue creation
static async completeAppointmentWithServices(
  id: number, 
  selectedServices: SelectedService[]
): Promise<{
  appointment: Appointment;
  revenueTransactions: any[];
  totalRevenue: number;
}> {
  
  

  if (selectedServices.length === 0) {
    // If no services, use the regular complete method
    const appointment = await this.completeAppointment(id);
    return {
      appointment,
      revenueTransactions: [],
      totalRevenue: 0,
    };
  }

  // Transform the selectedServices to match backend expectations
  const servicesData = selectedServices.map(selectedService => ({
    service_id: selectedService.service.id,
    custom_price: selectedService.customPrice || null,
    pricing_notes: selectedService.pricingReason || null,
    referrer_name: selectedService.referrer_name?.trim() || null,
  }));


  try {
    // Call the new complete-with-services endpoint
    const response = await this.request(`/appointments/${id}/complete-with-services`, {
      method: 'POST',
      body: JSON.stringify({
        services: servicesData,
      }),
    });


    // Calculate total revenue from the services
    const totalRevenue = selectedServices.reduce((total, service) => {
      const price = service.customPrice !== undefined 
        ? service.customPrice 
        : Number(service.service.base_fee);
      return total + price;
    }, 0);

    // FIXED: Just return the appointment data directly from the resource
    return {
      appointment: response.appointment, // AppointmentResource already has the right structure
      revenueTransactions: [], // Backend doesn't return full transaction details
      totalRevenue: response.appointment.amount_collected || totalRevenue,
    };

  } catch (error) {
    console.error('❌ Failed to complete appointment with services:', error);
    throw error;
  }
}

  // Mark appointment as no-show
  static async noShowAppointment(id: number): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/no-show`, {
      method: 'POST',
    });
    return response.data;
  }

  

  // Reschedule appointment
  static async rescheduleAppointment(
    id: number,
    data: {
      appointment_date: string;
      appointment_time: string;
      doctor_id?: number;
    }
  ): Promise<Appointment> {
    const response = await this.request(`/appointments/${id}/reschedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  }

  // Helper method for drag & drop reschedule (backward compatibility)
  static async rescheduleAppointmentByDrop(
    id: number, 
    newDate: string, 
    newTime: string, 
    doctorId?: number
  ): Promise<Appointment> {
    return this.rescheduleAppointment(id, {
      appointment_date: newDate,
      appointment_time: newTime,
      ...(doctorId && { doctor_id: doctorId })
    });
  }

  // Get available time slots
  static async getAvailableSlots(doctorId: number, date: string): Promise<TimeSlot[]> {
    const response = await this.request(
      `/appointments/available-slots/search?doctor_id=${doctorId}&date=${date}`
    );
    return response.data;
  }

  // Get available statuses for an appointment
  static async getAvailableStatuses(id: number): Promise<{
    current_status: string;
    available_statuses: Record<string, any>;
  }> {
    const response = await this.request(`/appointments/${id}/available-statuses`);
    return response;
  }

  // Get all appointment statuses with their properties
  static getAppointmentStatuses(): Record<string, any> {
    return {
      scheduled: {
        label: 'Scheduled',
        color: '#3b82f6', // Blue
        icon: '📅',
        next_statuses: ['confirmed', 'cancelled']
      },
      confirmed: {
        label: 'Confirmed', 
        color: '#10b981', // Green
        icon: '✅',
        next_statuses: ['arrived', 'no_show', 'cancelled']
      },
      arrived: {
        label: 'Arrived',
        color: '#8b5cf6', // Purple  
        icon: '🏥',
        next_statuses: ['completed', 'cancelled']
      },
      completed: {
        label: 'Completed',
        color: '#6b7280', // Gray
        icon: '✔️',
        next_statuses: []
      },
      cancelled: {
        label: 'Cancelled',
        color: '#ef4444', // Red
        icon: '❌', 
        next_statuses: []
      },
      no_show: {
        label: 'No Show',
        color: '#f59e0b', // Orange
        icon: '👻',
        next_statuses: ['cancelled']
      }
    };
  }

  // Get status info for a specific status
  static getStatusInfo(status: string): any {
    const statuses = this.getAppointmentStatuses();
    return statuses[status] || {
      label: status.charAt(0).toUpperCase() + status.slice(1),
      color: '#6b7280',
      icon: '📋',
      next_statuses: []
    };
  }

  // Get appointment types
  static getAppointmentTypes(): Array<{ value: string; label: string }> {
    return [
      { value: 'consultation', label: 'Consultation' },
      { value: 'follow-up', label: 'Follow-up' },
      { value: 'check-up', label: 'Check-up' },
      { value: 'emergency', label: 'Emergency' },
      { value: 'surgery', label: 'Surgery' },
      { value: 'therapy', label: 'Therapy' },
      { value: 'vaccination', label: 'Vaccination' },
      { value: 'lab-test', label: 'Lab Test' },
      { value: 'imaging', label: 'Imaging' },
      { value: 'other', label: 'Other' },
    ];
  }

  // Revenue and billing helper methods
  static async getAppointmentRevenue(appointmentId: number): Promise<{
    total_revenue: number;
    transactions: any[];
    breakdown: {
      clinic_total: number;
      provider_total: number;
      owner_total: number;
      referrer_total: number;
    };
  }> {
    try {
      const response = await this.request(`/revenue/by-appointment/${appointmentId}`);
      return response;
    } catch (error) {
      console.error('Failed to get appointment revenue:', error);
      return {
        total_revenue: 0,
        transactions: [],
        breakdown: {
          clinic_total: 0,
          provider_total: 0,
          owner_total: 0,
          referrer_total: 0,
        },
      };
    }
  }

  // Get revenue summary for reporting
  static async getRevenueSummary(params: {
    start_date?: string;
    end_date?: string;
    provider_id?: number;
    referrer_name?: string;
  } = {}): Promise<{
    total_revenue: number;
    total_transactions: number;
    clinic_revenue: number;
    provider_revenue: number;
    owner_revenue: number;
    referrer_revenue: number;
    breakdown_by_provider: any[];
    breakdown_by_referrer: any[];
  }> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await this.request(`/revenue/summary?${queryParams}`);
    return response;
  }
    // Add custom pricing save method
static async saveCustomPricing(
  appointmentId: number,
  services: SelectedService[]
): Promise<Appointment> {
  const response = await fetch(`${this.baseURL}/appointments/${appointmentId}/save-custom-pricing`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      services: services.map(service => ({
        service_id: service.service.id,
        custom_price: service.customPrice,
        pricing_notes: service.pricingReason,
        referrer_name: service.referrer_name,
      }))
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to save custom pricing');
  }

  const data = await response.json();
  return data.appointment;
}
/**
   * Edit a completed appointment with password verification
   */
  static async editCompletedAppointment(
    appointmentId: number,
    data: AppointmentEditRequest
  ): Promise<any> {
    const response = await this.request(`/appointments/${appointmentId}/edit-completed`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response;
  }

  /**
   * Mark completed appointment as incomplete
   */
  static async markAppointmentIncomplete(
    appointmentId: number,
    data: MarkIncompleteRequest
  ): Promise<any> {
    const response = await this.request(`/appointments/${appointmentId}/mark-incomplete`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response;
  }

  /**
   * Get available doctors for service filtering (admin/main secretary only)
   */
  static async getAvailableDoctors(): Promise<any[]> {
    try {
      const response = await this.request('/medical-services/available-doctors');
      return response;
    } catch (error: any) {
      if (error.message.includes('403') || error.message.includes('Unauthorized')) {
        return []; // User doesn't have permission
      }
      throw error;
    }
  }

  /**
   * Search medical services with real-time search
   */
  static async searchMedicalServices(
    query: string,
    doctorId?: string
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (query.trim()) {
      params.append("q", query.trim());
    }
    if (doctorId) {
      params.append("doctor_id", doctorId);
    }

    const response = await this.request(`/medical-services/search?${params}`);
    return response;
  }
// Add to AppointmentService class
static async applyTotalPriceAdjustment(
  appointmentId: number,
  adjustmentData: {
    total_amount: number;
    adjustment_type: 'custom_total' | 'discount';
    discount_percentage?: number;
    adjustment_reason?: string;
    services: Array<{
      service_id: number;
      adjusted_price: number;
    }>;
  }
): Promise<Appointment> {
  const response = await this.request(`/appointments/${appointmentId}/apply-total-adjustment`, {
    method: 'POST',
    body: JSON.stringify(adjustmentData),
  });
  
  // Debug the response structure
  // console.log('API Response:', response);
  
  // Handle different possible response structures
  if (response.appointment) {
    return response.appointment;
  } else if (response.data && response.data.appointment) {
    return response.data.appointment;
  } else if (response.data) {
    return response.data;
  } else {
    // If the response structure is unexpected, log it and return what we have
    console.error('Unexpected response structure:', response);
    throw new Error('Invalid response structure from server');
  }
}
  /**
   * Get medical services with filtering options
   */
  static async getMedicalServices(options: {
    search?: string;
    doctorId?: string;
    category?: string;
    serviceTypeId?: string;
    equipmentId?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    perPage?: number;
    paginate?: boolean;
  } = {}): Promise<any> {
    const params = new URLSearchParams();
    
    if (options.search) params.append("search", options.search);
    if (options.doctorId) params.append("doctor_id", options.doctorId);
    if (options.category) params.append("category", options.category);
    if (options.serviceTypeId) params.append("service_type_id", options.serviceTypeId);
    if (options.equipmentId) params.append("equipment_id", options.equipmentId);
    if (options.sortBy) params.append("sort_by", options.sortBy);
    if (options.sortOrder) params.append("sort_order", options.sortOrder);
    if (options.perPage) params.append("per_page", options.perPage.toString());
    if (options.paginate !== undefined) params.append("paginate", options.paginate.toString());

    const response = await this.request(`/medical-services?${params}`);
    return response;
  }
}

export default AppointmentService;